import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';

/**
 * @typedef {object} BundleRefs
 * @property {HTMLScriptElement} strings
 * @property {HTMLDetailsElement} summaryDetails
 * @property {HTMLElement} progressText
 * @property {HTMLProgressElement} progress
 * @property {HTMLOListElement} slots
 * @property {HTMLElement} subtotal
 * @property {HTMLElement} total
 * @property {HTMLElement | undefined} discountRow
 * @property {HTMLElement | undefined} discountAmount
 * @property {HTMLElement | undefined} discountOffer
 * @property {HTMLButtonElement} addButton
 * @property {HTMLElement} status
 * @extends {Component<BundleRefs>}
 */
export class BundleBuilder extends Component {
  requiredRefs = ['strings', 'summaryDetails', 'progressText', 'progress', 'slots', 'subtotal', 'total', 'addButton', 'status'];
  size = 4;
  selections = { 4: [], 5: [] };
  busy = false;
  reviewCart = false;
  controller;
  strings;
  visibilityObserver;
  seeded = new Set([4]);
  changingIndex = null;
  activeCategory = 'nails';

  connectedCallback() {
    super.connectedCallback();
    this.controller = new AbortController();
    this.initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    if (this.busy) {
      this.controller?.abort();
      this.controller = new AbortController();
      this.busy = false;
      this.reviewCart = true;
    }
    this.initialize();
    if (this.reviewCart) this.announce(this.text('error'));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.controller?.abort();
    this.visibilityObserver?.disconnect();
  }

  initialize() {
    this.visibilityObserver?.disconnect();
    this.visibilityObserver = new IntersectionObserver(([entry]) => { this.dataset.inView = String(entry.isIntersecting); });
    this.visibilityObserver.observe(this);
    this.strings = JSON.parse(this.refs.strings.textContent);
    this.querySelector('.bundle-builder__enhanced').hidden = false;
    const fallback = this.querySelector('[data-bundle-fallback]');
    if (fallback) fallback.hidden = true;
    this.refs.summaryDetails.open = !window.matchMedia('(max-width: 749px)').matches;
    // Reconcile saved choices with newly rendered merchant settings and variant data.
    for (const size of [4, 5]) {
      this.selections[size] = this.selections[size].flatMap((item) => {
        const card = this.cards.find((card) => card.dataset.productId === item.productId && card.dataset.category === item.category);
        const option = card?.querySelector(`option[value="${item.id}"]`);
        if (!option || !this.available(option)) return [];
        return [{ ...item, price: Number(option.dataset.price) }];
      });
    }
    this.showCategory(this.activeCategory);
    this.render();
  }

  get cards() { return [...this.querySelectorAll('[data-card]')]; }
  get panels() { return [...this.querySelectorAll('[data-panel]')]; }
  get items() { return this.selections[this.size]; }
  get count() { return this.items.reduce((count, item) => count + item.quantity, 0); }
  get subtotal() { return this.items.reduce((sum, item) => sum + item.price * item.quantity, 0); }
  get savingsAvailable() {
    return this.dataset.currency === 'USD' && Number(this.dataset.discountAmount) > 0 && this.panels.every(panel => this.cards.some(card => card.dataset.category === panel.dataset.panel && card.dataset.discountEligible === 'true'));
  }
  get discount() {
    return this.savingsAvailable && this.complete() && this.items.every(item => this.cards.some(card => card.dataset.productId === item.productId && card.dataset.category === item.category && card.dataset.discountEligible === 'true')) ? Number(this.dataset.discountAmount) : 0;
  }
  categoryMax(category) { return category === 'nails' && this.size === 5 ? 2 : 1; }
  categoryCount(category) { return this.items.filter((item) => item.category === category).reduce((sum, item) => sum + item.quantity, 0); }
  available(option) {
    return option.dataset.available === 'true' && Number(option.dataset.min || 1) === 1 && Number(option.dataset.increment || 1) === 1 && this.variantLimit(option) > 0;
  }
  variantLimit(option) {
    const limits = [option.dataset.max, option.dataset.stock].filter(value => value !== undefined && value !== '').map(Number);
    return limits.length ? Math.min(...limits) : Infinity;
  }
  selectedVariantCount(id, items = this.items) { return items.filter(item => item.id === id).reduce((sum, item) => sum + item.quantity, 0); }
  selectedOption(item) {
    const card = this.cards.find(card => card.dataset.productId === item.productId && card.dataset.category === item.category);
    return card?.querySelector(`option[value="${item.id}"]`);
  }
  money(cents) { return new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: this.dataset.currency }).format(cents / 100); }
  text(key, values = {}) {
    return Object.entries(values).reduce((text, [key, value]) => text.replaceAll(new RegExp(`{{\\s*${key}\\s*}}`, 'g'), String(value)).replaceAll(`[${key}]`, String(value)), this.strings[key]);
  }
  announce(text) { this.refs.status.textContent = text; }
  complete() {
    return this.count === this.size && this.items.every((item) => {
      const card = this.cards.find((card) => card.dataset.productId === item.productId && card.dataset.category === item.category);
      const option = card?.querySelector(`option[value="${item.id}"]`);
      return option && this.available(option) && this.selectedVariantCount(item.id) <= this.variantLimit(option);
    }) && this.panels.every((panel) => this.categoryCount(panel.dataset.panel) === this.categoryMax(panel.dataset.panel));
  }

  /** @param {Event} event */
  changeSize(event) {
    if (this.busy || !(event.target instanceof HTMLInputElement)) return;
    this.changingIndex = null;
    const old = this.items;
    const size = Number(event.target.value);
    if (![4, 5].includes(size)) return;
    this.size = size;
    // Seed an untouched option with compatible choices; retain both drafts afterwards.
    if (!this.seeded.has(this.size)) {
      this.seeded.add(this.size);
      for (const item of old) {
        const quantity = Math.min(item.quantity, this.size - this.count, this.categoryMax(item.category) - this.categoryCount(item.category));
        if (quantity > 0) this.items.push({ ...item, quantity });
      }
    }
    this.render();
    this.announce(this.selectionProgress());
  }

  /** @param {MouseEvent} event */
  changeCategory(event) {
    if (!(event.target instanceof HTMLElement)) return;
    this.showCategory(event.target.dataset.category);
    if (event.target.closest('.bundle-builder__slots')) {
      this.refs.summaryDetails.open = false;
      this.panels.find(panel => panel.dataset.panel === this.activeCategory)?.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }
  }

  showCategory(category) {
    this.activeCategory = category;
    for (const panel of this.panels) panel.hidden = panel.dataset.panel !== category;
    for (const button of this.querySelectorAll('.bundle-builder__categories button')) button.setAttribute('aria-pressed', String(button.dataset.category === category));
  }

  /** @param {Event} event */
  changeVariant(event) {
    if (this.busy || !(event.target instanceof HTMLSelectElement)) return;
    // A variant choice changes the card. Existing bundle entries remain explicit until replaced.
    this.updateCard(event.target.closest('[data-card]'));
  }

  updateCard(card) {
    const option = card.querySelector('[data-variant]').selectedOptions[0];
    const selected = this.items.find((item) => item.id === option.value && item.category === card.dataset.category);
    card.dataset.selected = String(Boolean(selected));
    card.querySelector('[data-price]').textContent = option.dataset.money;
    card.querySelector('[data-availability]').textContent = this.text(this.available(option) ? 'available' : 'unavailable');
    const image = card.querySelector('img');
    if (image && (option.dataset.image || card.dataset.image)) { image.src = option.dataset.image || card.dataset.image; image.removeAttribute('srcset'); }
    const button = card.querySelector('[data-select]');
    const twoKits = card.dataset.category === 'nails' && this.size === 5;
    const replacing = this.changingIndex !== null && this.items[this.changingIndex]?.category === card.dataset.category;
    const full = this.categoryCount(card.dataset.category) >= this.categoryMax(card.dataset.category) || this.count >= this.size;
    const stockFull = this.selectedVariantCount(option.value) >= this.variantLimit(option);
    button.disabled = this.busy || !this.available(option) || (twoKits && !replacing && (full || stockFull));
    const quantityControls = card.querySelector('[data-card-quantity]');
    if (quantityControls) {
      quantityControls.hidden = !twoKits || !selected;
      card.querySelector('[data-card-count]').textContent = String(selected?.quantity || 0);
      for (const control of quantityControls.querySelectorAll('button')) {
        control.dataset.index = String(this.items.indexOf(selected));
        control.disabled = this.busy || (control.dataset.action === 'plus' && (full || stockFull || !this.available(option)));
      }
    }
    const badge = card.querySelector('[data-quantity-badge]');
    if (badge) { badge.hidden = !twoKits || selected?.quantity !== 2; badge.textContent = '×2'; }
    button.textContent = selected ? `${this.text('selected')} (${selected.quantity})` : this.text('select');
    button.setAttribute('aria-label', `${button.textContent} — ${card.querySelector('[data-title]').textContent}`);
  }

  /** @param {MouseEvent} event */
  selectProduct(event) {
    if (this.busy || !(event.target instanceof HTMLElement)) return;
    const card = event.target.closest('[data-card]');
    if (!card) return;
    const option = card.querySelector('[data-variant]').selectedOptions[0];
    if (!this.available(option)) return;
    const category = card.dataset.category;
    const replacing = this.changingIndex !== null ? this.items[this.changingIndex] : null;
    if (replacing && replacing.category !== category) { this.announce(this.text('replace_category')); return; }
    const replaceSingleCategory = this.categoryMax(category) === 1;
    const nextItems = this.items.map(item => ({ ...item }));
    if (replacing) {
      const previous = nextItems[this.changingIndex];
      if (previous.quantity > 1) previous.quantity--;
      else nextItems.splice(this.changingIndex, 1);
    } else if (replaceSingleCategory) {
      for (let index = nextItems.length - 1; index >= 0; index--) if (nextItems[index].category === category) nextItems.splice(index, 1);
    }
    const nextCount = nextItems.reduce((sum, item) => sum + item.quantity, 0);
    const categoryCount = nextItems.filter(item => item.category === category).reduce((sum, item) => sum + item.quantity, 0);
    if (nextCount >= this.size || categoryCount >= this.categoryMax(category)) { this.announce(this.text('limit')); return; }
    if (this.selectedVariantCount(option.value, nextItems) + 1 > this.variantLimit(option)) { this.announce(this.text('stock_limit')); return; }
    this.selections[this.size] = nextItems;
    this.changingIndex = null;
    const existing = this.items.find(item => item.id === option.value && item.category === category);
    if (existing) existing.quantity++;
    else this.items.push({ id: option.value, productId: card.dataset.productId, url: card.dataset.productUrl, category, title: card.querySelector('[data-title]').textContent, variant: card.dataset.defaultVariant === 'true' ? '' : option.textContent.trim(), price: Number(option.dataset.price), image: option.dataset.image || card.querySelector('img')?.src, quantity: 1 });
    this.render();
    if (event.target instanceof HTMLButtonElement && event.target.disabled) card.querySelector('[data-action="minus"]')?.focus();
    this.announce(this.selectionProgress());
  }

  /** @param {MouseEvent} event */
  editItem(event) {
    if (this.busy || !(event.target instanceof HTMLButtonElement)) return;
    const index = Number(event.target.dataset.index);
    const item = this.items[index];
    if (!item) return;
    const action = event.target.dataset.action;
    const originCard = event.target.closest('[data-card]');
    if (action === 'change') {
      this.changingIndex = index;
      this.showCategory(item.category);
      const card = this.cards.find((card) => card.dataset.productId === item.productId && card.dataset.category === item.category);
      const select = card?.querySelector('[data-variant]');
      if (select) { select.value = item.id; this.updateCard(card); }
      for (const currentCard of this.cards) this.updateCard(currentCard);
      (card?.querySelector('[data-select]') || this.querySelector('.bundle-builder__categories button')).focus();
      return;
    }
    if (action === 'plus') {
      if (this.count >= this.size || this.categoryCount(item.category) >= this.categoryMax(item.category)) { this.announce(this.text('limit')); return; }
      const option = this.selectedOption(item);
      if (!option || this.selectedVariantCount(item.id) + 1 > this.variantLimit(option)) { this.announce(this.text('stock_limit')); return; }
      item.quantity++;
    } else if (action === 'minus' && item.quantity > 1) item.quantity--;
    else this.items.splice(index, 1);
    this.changingIndex = null;
    this.render();
    const focusIndex = Math.min(index, this.items.length - 1);
    if (originCard) {
      const controls = originCard.querySelector('[data-card-quantity]');
      const focusTarget = controls && !controls.hidden ? controls.querySelector(`[data-action="${action}"]:not(:disabled)`) || controls.querySelector('[data-action="minus"]') : originCard.querySelector('[data-select]');
      focusTarget?.focus();
    } else (this.refs.slots.querySelector(`[data-index="${focusIndex}"]`) || this.refs.slots.querySelector('button'))?.focus();
    this.announce(this.selectionProgress());
  }

  selectionProgress() {
    const progress = this.text('progress', { count: this.count, total: this.size });
    const selection = this.size === 5 ? `${this.text('category_progress', { count: this.categoryCount('nails'), total: 2, category: this.text('nails') })}. ${progress}` : progress;
    return this.discount ? `${selection}. ${this.text('savings')}: ${this.money(this.discount)}` : selection;
  }

  render() {
    for (const radio of this.querySelectorAll('input[type="radio"]')) { radio.checked = Number(radio.value) === this.size; radio.disabled = this.busy; }
    for (const card of this.cards) {
      card.querySelector('[data-variant]').disabled = this.busy;
      this.updateCard(card);
    }
    for (const panel of this.panels) panel.querySelector('[data-category-rule]').textContent = this.categoryMax(panel.dataset.panel) === 2 ? this.text('category_progress', { count: this.categoryCount(panel.dataset.panel), total: 2, category: this.text(panel.dataset.panel) }) : this.text('required');
    this.refs.progressText.textContent = this.text('progress', { count: this.count, total: this.size });
    this.refs.progress.max = this.size;
    this.refs.progress.value = this.count;
    this.refs.subtotal.textContent = this.money(this.subtotal);
    const discount = this.discount;
    if (this.refs.discountOffer) this.refs.discountOffer.hidden = !this.savingsAvailable;
    if (this.refs.discountRow) this.refs.discountRow.hidden = !discount;
    if (this.refs.discountAmount) this.refs.discountAmount.textContent = `−${this.money(discount)}`;
    this.refs.total.textContent = this.money(this.subtotal - discount);
    this.refs.addButton.disabled = this.busy || this.reviewCart || !this.complete();
    this.refs.addButton.textContent = this.text(this.busy ? 'adding' : 'add');
    this.setAttribute('aria-busy', String(this.busy));
    const slots = [];
    const addSlot = (item, label, index) => {
      const li = document.createElement('li'); li.className = 'bundle-builder__slot';
      if (item?.image) { const img = document.createElement('img'); img.src = item.image; img.alt = ''; img.width = 48; img.height = 48; li.append(img); }
      const content = document.createElement('div'); content.className = 'bundle-builder__slot-content';
      const title = document.createElement('strong'); title.textContent = item ? item.title : label; content.append(title);
      const detail = document.createElement('small'); detail.textContent = item ? `${item.variant ? `${item.variant} · ` : ''}${this.money(item.price)} × ${item.quantity}${item.quantity > 1 ? ` = ${this.money(item.price * item.quantity)}` : ''}` : this.text('choose'); content.append(detail); li.append(content);
      const controls = document.createElement('div'); controls.className = 'bundle-builder__controls';
      const actions = item ? (item.category === 'nails' && this.size === 5 ? ['minus', 'plus', 'change', 'remove'] : ['change', 'remove']) : ['choose'];
      for (const action of actions) {
        const button = document.createElement('button'); button.type = 'button'; button.textContent = action === 'plus' ? '+' : action === 'minus' ? '−' : this.text(action);
        button.setAttribute('aria-label', `${this.text(action)} ${item?.title || label}`);
        button.disabled = this.busy;
        if (action === 'plus') button.disabled ||= this.count >= this.size || this.categoryCount(item.category) >= this.categoryMax(item.category);
        if (item) { button.dataset.index = index; button.dataset.action = action; button.setAttribute('on:click', '/editItem'); }
        else { button.dataset.category = this.activeCategory; button.setAttribute('on:click', '/changeCategory'); }
        controls.append(button);
      }
      li.append(controls); slots.push(li);
    };
    for (const panel of this.panels) {
      const category = panel.dataset.panel;
      this.items.forEach((item, index) => { if (item.category === category) addSlot(item, this.text(category), index); });
      const missing = this.categoryMax(category) - this.categoryCount(category);
      for (let index = 0; index < missing; index++) {
        const label = this.categoryMax(category) > 1 ? this.text('category_slot', { category: this.text(category), number: this.categoryCount(category) + index + 1 }) : this.text(category);
        addSlot(null, label, -1);
        slots[slots.length - 1].querySelector('button').dataset.category = category;
      }
    }
    this.refs.slots.replaceChildren(...slots);
  }

  /** @param {MouseEvent} event */
  async addBundle(event) {
    event.preventDefault();
    if (this.busy || this.reviewCart) return;
    if (!this.complete()) { this.announce(this.text('incomplete')); return; }
    const expectedSavings = this.discount;
    this.busy = true; this.render(); this.announce(this.text('adding'));
    const signal = this.controller.signal;
    const root = window.Shopify.routes.root;
    const bundleId = crypto.randomUUID();
    const submittedItems = this.items.map(item => ({ ...item }));
    let submitted = false;
    try {
      const cartResponse = await fetch(`${root}cart.js`, { signal });
      if (!cartResponse.ok) throw new Error(this.text('error'));
      const before = await cartResponse.json();
      // Recheck current variants and contextual prices immediately before submitting.
      const products = new Map();
      for (const item of submittedItems) {
        if (!products.has(item.productId)) {
          const response = await fetch(`${item.url}.js`, { signal, cache: 'no-store' });
          if (!response.ok) throw new Error(this.text('stock_changed'));
          products.set(item.productId, await response.json());
        }
        const variant = products.get(item.productId).variants.find((variant) => String(variant.id) === item.id);
        const option = this.cards.find((card) => card.dataset.productId === item.productId)?.querySelector(`option[value="${item.id}"]`);
        const desired = this.selectedVariantCount(item.id, submittedItems);
        const existing = before.items.filter((line) => String(line.variant_id) === item.id).reduce((sum, line) => sum + line.quantity, 0);
        if (!variant?.available) {
          if (option) option.dataset.available = 'false';
          throw new Error(this.text('stock_changed'));
        }
        if (variant.price !== item.price && option) {
          option.dataset.price = String(variant.price);
          option.dataset.money = this.money(variant.price);
          for (const draft of Object.values(this.selections)) {
            for (const choice of draft) if (choice.id === item.id) choice.price = variant.price;
          }
          throw new Error(this.text('stock_changed'));
        }
        if (!option || !this.available(option) || desired + existing > this.variantLimit(option)) throw new Error(this.text('stock_changed'));
      }
      const sections = [...new Set([...document.querySelectorAll('cart-items-component')].map((element) => element.dataset.sectionId).filter(Boolean))].slice(0, 5);
      submitted = true;
      const response = await fetch(`${root}cart/add.js`, { method: 'POST', signal, headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: submittedItems.map((item) => ({ id: item.id, quantity: item.quantity, properties: { _tipzy_bundle_id: bundleId, _tipzy_bundle_size: String(this.size), _tipzy_bundle_category: item.category } })), sections, sections_url: window.location.pathname }) });
      const added = await response.json();
      const confirmation = await fetch(`${root}cart.js`, { signal, cache: 'no-store' });
      if (!confirmation.ok) throw new Error(this.text('error'));
      const cart = await confirmation.json();
      const lines = cart.items.filter((line) => line.properties?._tipzy_bundle_id === bundleId);
      const complete = submittedItems.every((item) => lines.some((line) => String(line.variant_id) === item.id && line.properties._tipzy_bundle_category === item.category && line.quantity === item.quantity)) && lines.reduce((sum, line) => sum + line.quantity, 0) === this.size;
      this.dispatchEvent(new CartAddEvent(cart, this.id, { source: 'bundle-builder', itemCount: cart.item_count, sections: added.sections }));
      if (!response.ok || !complete) { this.reviewCart = true; throw new Error(added.description || this.text('error')); }
      // SMART allocates savings across matching cart lines, so a group's allocations
      // can move by a cent. Confirm the cart's additional savings, not each line's share.
      if (expectedSavings && Number(cart.total_discount || 0) - Number(before.total_discount || 0) < expectedSavings) {
        this.reviewCart = true;
        this.announce(this.text('discount_error'));
        return;
      }
      this.announce(this.text('success'));
      if (!document.querySelector('cart-drawer-component')) window.location.assign(`${root}cart`);
    } catch (error) {
      if (signal.aborted) return;
      // A failed request may still have reached Shopify. Block retries until the cart is reviewed.
      if (submitted) this.reviewCart = true;
      this.announce(submitted ? this.text('error') : error.message || this.text('error'));
    } finally {
      this.busy = false;
      if (this.isConnected) this.render();
    }
  }
}
if (!customElements.get('bundle-builder')) customElements.define('bundle-builder', BundleBuilder);
